import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import AppNav from '../../components/app/AppNav';
import { Event } from '../../types/events';
import { EVENT_EMOJI, EVENT_GRADIENT } from '../../constants/events';
import { apiGet } from '../../api/api';
import { useAuth } from '../../context/AuthContext';
import { useUserLocation, calcDistance, formatDistance } from '../../hooks/useUserLocation';
import { slugify, extractId } from '../../utils/slug';

function formatDate(iso: string) {
  try {
    return new Date(iso).toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch { return iso; }
}

function toICSDate(iso: string) {
  return new Date(iso).toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
}

export default function AppEventDetail() {
  const { id: slugParam } = useParams<{ id: string }>();
  const id = slugParam ? extractId(slugParam) : undefined;
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const community = (location.state as { community?: string } | null)?.community || searchParams.get('community') || undefined;
  const { authUser, loading: authLoading } = useAuth();
  const userCoords = useUserLocation();

  const [event, setEvent] = useState<Event | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [imgIndex, setImgIndex] = useState(0);
  const [copied, setCopied] = useState(false);
  const [showCalMenu, setShowCalMenu] = useState(false);

  useEffect(() => {
    if (!id || authLoading) return;
    const params: Record<string, string> = { id };
    if (!authUser && community) params.community = community;
    const endpoint = authUser ? '/api-event-details' : '/api-event-details-public';
    apiGet<{ data: Event }>(endpoint, params, !!authUser)
      .then((res) => setEvent(res.data))
      .catch((err: unknown) => setError(err instanceof Error ? err.message : 'Failed to load event'))
      .finally(() => setLoading(false));
  }, [id, community, authUser, authLoading]);

  const handleShare = () => {
    const link = event
      ? `https://sahayy.com/events/${slugify(event.title)}-${event.id}?community=${encodeURIComponent(event.community || '')}`
      : window.location.href;
    const dateStr = event ? new Date(event.event_date).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' }) : '';
    const timeStr = event ? new Date(event.event_date).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }) : '';
    const message = event
      ? `🎉 ${event.title}\n📅 ${dateStr} at ${timeStr}${event.location ? `\n📍 ${event.location}` : ''}${event.community ? `\n👥 ${event.community}` : ''}\n\nJoin us on Sahayy 👇\n${link}`
      : link;
    navigator.clipboard.writeText(message).then(() => { setCopied(true); setTimeout(() => setCopied(false), 2000); });
  };

  const handleGoogleCalendar = () => {
    if (!event) return;
    const start = toICSDate(event.event_date);
    const end = event.end_time ? toICSDate(event.end_time) : toICSDate(new Date(new Date(event.event_date).getTime() + 2 * 60 * 60 * 1000).toISOString());
    const url = `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(event.title)}&dates=${start}/${end}&details=${encodeURIComponent(event.description || '')}&location=${encodeURIComponent(event.location || '')}`;
    window.open(url, '_blank', 'noopener,noreferrer');
    setShowCalMenu(false);
  };

  const handleDownloadICS = () => {
    if (!event) return;
    const start = toICSDate(event.event_date);
    const end = event.end_time ? toICSDate(event.end_time) : toICSDate(new Date(new Date(event.event_date).getTime() + 2 * 60 * 60 * 1000).toISOString());
    const ics = [
      'BEGIN:VCALENDAR', 'VERSION:2.0', 'PRODID:-//Sahayy//EN',
      'BEGIN:VEVENT',
      `DTSTART:${start}`, `DTEND:${end}`,
      `SUMMARY:${event.title}`,
      `DESCRIPTION:${(event.description || '').replace(/\n/g, '\\n')}`,
      `LOCATION:${event.location || ''}`,
      `URL:${window.location.href}`,
      'END:VEVENT', 'END:VCALENDAR',
    ].join('\r\n');
    const blob = new Blob([ics], { type: 'text/calendar' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `${event.title.replace(/\s+/g, '_')}.ics`;
    a.click();
    URL.revokeObjectURL(a.href);
    setShowCalMenu(false);
  };

  if (loading) return (
    <div style={{ minHeight: '100vh', backgroundColor: '#fff' }}>
      <AppNav />
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 'calc(100vh - 64px)', flexDirection: 'column', gap: '0.75rem' }}>
        <div style={{ width: '36px', height: '36px', border: '3px solid #f0f0f0', borderTop: '3px solid #FF385C', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    </div>
  );

  if (error || !event) return (
    <div style={{ minHeight: '100vh', backgroundColor: '#fff' }}>
      <AppNav />
      <div style={{ maxWidth: '480px', margin: '6rem auto', padding: '0 1.5rem', textAlign: 'center' }}>
        <p style={{ fontSize: '2.5rem', margin: '0 0 1rem' }}>📅</p>
        <h2 style={{ color: '#111', fontWeight: 700, margin: '0 0 0.5rem' }}>Event not found</h2>
        <p style={{ color: '#6b7280', margin: '0 0 2rem' }}>{error || 'This event does not exist.'}</p>
        <button onClick={() => navigate('/events')} style={{ padding: '0.75rem 2rem', backgroundColor: '#111', border: 'none', borderRadius: '10px', color: '#fff', fontWeight: 600, cursor: 'pointer' }}>← Back to Events</button>
      </div>
    </div>
  );

  const isCreator = authUser?.userId === event.user_id;
  const emoji = EVENT_EMOJI[event.category] || '📅';
  const gradient = EVENT_GRADIENT[event.category] || EVENT_GRADIENT['Other'];
  const images = event.images?.filter(Boolean) || [];
  const hasImage = images.length > 0;
  const isMobileDevice = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
  const deepLink = `sahayy://events/${event.id}?community=${encodeURIComponent(event.community || '')}`;

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#fff' }} onClick={() => setShowCalMenu(false)}>
      <AppNav />

      <div style={{ maxWidth: '1080px', margin: '0 auto', padding: '1.25rem 1.5rem 8rem' }}>
        {/* Back + Share */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.875rem' }}>
          <button onClick={() => navigate('/events')} style={{ background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.375rem', color: '#111', fontWeight: 600, fontSize: '0.9rem', padding: 0 }}>← All Events</button>
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
            {isCreator && (
              <button onClick={() => navigate(`/events/edit?id=${event.id}&community=${encodeURIComponent(event.community || '')}`)}
                style={{ background: 'none', border: '1px solid #e5e7eb', borderRadius: '20px', padding: '0.35rem 1rem', cursor: 'pointer', fontSize: '0.82rem', fontWeight: 600, color: '#111' }}>✏️ Edit</button>
            )}
            <button onClick={handleShare} style={{ background: 'none', border: '1px solid #e5e7eb', borderRadius: '20px', padding: '0.35rem 1rem', cursor: 'pointer', fontSize: '0.82rem', fontWeight: 600, color: copied ? '#16a34a' : '#111' }}>{copied ? '✓ Copied' : '↑ Share'}</button>
          </div>
        </div>

        {/* Photo grid */}
        <div className="hero-grid" style={{ display: 'grid', gap: '6px', gridTemplateColumns: '1fr 1fr', gridTemplateRows: '420px', borderRadius: '16px', overflow: 'hidden' }}>
          <div style={{ position: 'relative', overflow: 'hidden' }}>
            {images[0] ? <img src={images[0]} alt={event.title} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
              : <div style={{ width: '100%', height: '100%', background: gradient, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><span style={{ fontSize: '6rem' }}>{emoji}</span></div>}
          </div>
          <div style={{ display: 'grid', gap: '6px', gridTemplateColumns: '1fr 1fr', gridTemplateRows: '1fr 1fr' }}>
            {[1, 2, 3, 4].map((idx) => (
              <div key={idx} style={{ position: 'relative', overflow: 'hidden' }}>
                {images[idx]
                  ? <img src={images[idx]} alt={`${event.title} ${idx + 1}`} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                  : <div style={{ width: '100%', height: '100%', background: 'linear-gradient(135deg, #f0f0ee 0%, #e8e6e3 100%)' }} />}
              </div>
            ))}
          </div>
        </div>

        {/* Mobile carousel */}
        <div className="hero-carousel" style={{ position: 'relative', width: '100%', aspectRatio: '16/9', borderRadius: '16px', overflow: 'hidden', backgroundColor: '#f3f4f6' }}>
          {hasImage ? <img key={imgIndex} src={images[imgIndex]} alt={event.title} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
            : <div style={{ width: '100%', height: '100%', background: gradient, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><span style={{ fontSize: '5rem' }}>{emoji}</span></div>}
          {images.length > 1 && (
            <>
              <button onClick={() => setImgIndex(i => (i - 1 + images.length) % images.length)} style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', background: 'rgba(255,255,255,0.88)', border: 'none', borderRadius: '50%', width: '34px', height: '34px', cursor: 'pointer', fontSize: '1rem' }}>‹</button>
              <button onClick={() => setImgIndex(i => (i + 1) % images.length)} style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)', background: 'rgba(255,255,255,0.88)', border: 'none', borderRadius: '50%', width: '34px', height: '34px', cursor: 'pointer', fontSize: '1rem' }}>›</button>
              <div style={{ position: 'absolute', bottom: '10px', left: 0, right: 0, display: 'flex', justifyContent: 'center', gap: '5px' }}>
                {images.map((_, i) => <div key={i} onClick={() => setImgIndex(i)} style={{ width: i === imgIndex ? '16px' : '6px', height: '6px', borderRadius: '3px', backgroundColor: i === imgIndex ? '#fff' : 'rgba(255,255,255,0.5)', cursor: 'pointer', transition: 'all 0.2s' }} />)}
              </div>
            </>
          )}
        </div>

        {/* Content */}
        <div className="detail-columns" style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) 340px', gap: '4rem', alignItems: 'start' }}>
          {/* Left */}
          <div>
            <div style={{ padding: '2rem 0 1.5rem', borderBottom: '1px solid #f0f0f0' }}>
              <h1 style={{ color: '#111', fontWeight: 800, fontSize: 'clamp(1.5rem,3vw,2rem)', margin: '0 0 0.75rem', letterSpacing: '-0.03em', lineHeight: 1.2 }}>{event.title}</h1>
              <div style={{ display: 'flex', alignItems: 'center', gap: '1.25rem', flexWrap: 'wrap', color: '#6b7280', fontSize: '0.9rem' }}>
                <span style={{ fontWeight: 600, color: '#111' }}>{emoji} {event.category}</span>
                {event.location && (
                  <a href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(event.location)}`} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()} style={{ color: 'inherit', textDecoration: 'none' }}>
                    📍 {event.location}
                    {userCoords && event.latitude && event.longitude && (
                      <span style={{ color: '#10b981', marginLeft: '6px', fontSize: '0.82rem', fontWeight: 600 }}>{formatDistance(calcDistance(userCoords.lat, userCoords.lng, event.latitude, event.longitude))}</span>
                    )}
                    {' '}<span style={{ color: '#FF385C', fontSize: '0.75rem', fontWeight: 600 }}>↗</span>
                  </a>
                )}
                {event.community && <span style={{ color: '#9ca3af' }}>{event.community}</span>}
              </div>
            </div>

            <div style={{ padding: '1.5rem 0', borderBottom: '1px solid #f0f0f0' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                <div style={{ width: '48px', height: '48px', borderRadius: '12px', backgroundColor: '#f9fafb', border: '1px solid #e5e7eb', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.4rem', flexShrink: 0 }}>📅</div>
                <div>
                  <p style={{ margin: 0, fontWeight: 700, fontSize: '0.95rem', color: '#111' }}>{formatDate(event.event_date)}</p>
                  {event.end_time && <p style={{ margin: '2px 0 0', fontSize: '0.82rem', color: '#9ca3af' }}>Ends {formatDate(event.end_time)}</p>}
                </div>
              </div>
              {event.attendees_count != null && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginTop: '1rem' }}>
                  <div style={{ width: '48px', height: '48px', borderRadius: '12px', backgroundColor: '#f9fafb', border: '1px solid #e5e7eb', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.4rem', flexShrink: 0 }}>👥</div>
                  <p style={{ margin: 0, fontWeight: 600, fontSize: '0.95rem', color: '#111' }}>
                    {event.attendees_count} attending{event.max_attendees ? ` · ${event.max_attendees} max` : ''}
                  </p>
                </div>
              )}
            </div>

            <div style={{ padding: '1.5rem 0' }}>
              <h2 style={{ margin: '0 0 0.875rem', fontWeight: 700, fontSize: '1rem', color: '#111' }}>About this event</h2>
              <p style={{ color: '#374151', fontSize: '0.975rem', lineHeight: 1.8, margin: 0, whiteSpace: 'pre-line' }}>{event.description}</p>
            </div>
          </div>

          {/* Right sticky */}
          <div className="detail-sidebar" style={{ position: 'sticky', top: '80px', paddingTop: '2rem' }}>
            <div style={{ border: '1px solid #e5e7eb', borderRadius: '16px', padding: '1.5rem', boxShadow: '0 6px 30px rgba(0,0,0,0.08)' }}>
              <p style={{ margin: '0 0 0.25rem', fontWeight: 800, fontSize: '1rem', color: '#111' }}>{new Date(event.event_date).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' })}</p>
              <p style={{ margin: '0 0 1.25rem', fontSize: '0.82rem', color: '#9ca3af' }}>{new Date(event.event_date).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}</p>

              {event.ticket_url ? (
                <a href={event.ticket_url} target="_blank" rel="noopener noreferrer" style={{ display: 'block', textAlign: 'center', padding: '0.875rem', background: 'linear-gradient(135deg,#FF385C,#e11d48)', color: '#fff', borderRadius: '10px', fontWeight: 600, fontSize: '0.95rem', textDecoration: 'none', marginBottom: '0.75rem', boxShadow: '0 4px 14px rgba(255,56,92,0.35)' }}>Get Tickets →</a>
              ) : (
                <a href={isMobileDevice ? deepLink : 'https://sahayy.com/download'} target={isMobileDevice ? undefined : '_blank'} rel="noopener noreferrer" style={{ display: 'block', textAlign: 'center', padding: '0.875rem', backgroundColor: '#111', color: '#fff', borderRadius: '10px', fontWeight: 600, fontSize: '0.95rem', textDecoration: 'none', marginBottom: '0.75rem' }}>Open in App →</a>
              )}

              <button onClick={handleShare} style={{ width: '100%', padding: '0.75rem', backgroundColor: '#fff', border: '1.5px solid #e5e7eb', borderRadius: '10px', color: '#111', fontSize: '0.9rem', fontWeight: 500, cursor: 'pointer', marginBottom: '0.5rem' }}>{copied ? '✓ Message copied!' : '↑ Share event'}</button>

              <div style={{ position: 'relative' }}>
                <button onClick={() => setShowCalMenu(v => !v)} style={{ width: '100%', padding: '0.75rem', backgroundColor: '#fff', border: '1.5px solid #e5e7eb', borderRadius: '10px', color: '#111', fontSize: '0.9rem', fontWeight: 500, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.4rem' }}>
                  📅 Add to Calendar
                </button>
                {showCalMenu && (
                  <div onClick={e => e.stopPropagation()} style={{ position: 'absolute', top: 'calc(100% + 6px)', left: 0, right: 0, backgroundColor: '#fff', border: '1px solid #e5e7eb', borderRadius: '10px', boxShadow: '0 8px 24px rgba(0,0,0,0.1)', zIndex: 10, overflow: 'hidden' }}>
                    <button onClick={handleGoogleCalendar} style={{ width: '100%', padding: '0.75rem 1rem', background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left', fontSize: '0.875rem', fontWeight: 500, color: '#111', display: 'flex', alignItems: 'center', gap: '0.6rem', borderBottom: '1px solid #f3f4f6' }}>
                      <span style={{ fontSize: '1rem' }}>🗓</span> Google Calendar
                    </button>
                    <button onClick={handleDownloadICS} style={{ width: '100%', padding: '0.75rem 1rem', background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left', fontSize: '0.875rem', fontWeight: 500, color: '#111', display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
                      <span style={{ fontSize: '1rem' }}>📥</span> Apple / Outlook (.ics)
                    </button>
                  </div>
                )}
              </div>

              <div style={{ marginTop: '1.25rem', padding: '1rem', backgroundColor: '#f9fafb', borderRadius: '10px', textAlign: 'center' }}>
                <p style={{ margin: '0 0 0.5rem', fontSize: '0.82rem', color: '#374151', fontWeight: 600 }}>Get the full experience</p>
                <a href={isMobileDevice ? deepLink : 'https://sahayy.com/download'} target={isMobileDevice ? undefined : '_blank'} rel="noopener noreferrer" style={{ display: 'inline-block', backgroundColor: '#111', color: '#fff', padding: '0.5rem 1.25rem', borderRadius: '8px', fontSize: '0.82rem', fontWeight: 600, textDecoration: 'none' }}>Open in App →</a>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile bottom bar */}
      <div className="mobile-bottom-bar" style={{ position: 'fixed', bottom: 0, left: 0, right: 0, backgroundColor: '#fff', borderTop: '1px solid #f0f0f0', padding: '0.875rem 1.25rem', display: 'flex', alignItems: 'center', gap: '0.625rem', zIndex: 50 }}>
        <button onClick={() => setShowCalMenu(v => !v)} style={{ padding: '0.75rem', backgroundColor: '#f3f4f6', border: 'none', borderRadius: '10px', color: '#111', fontSize: '1.1rem', cursor: 'pointer', flexShrink: 0 }} title="Add to Calendar">📅</button>
        {showCalMenu && (
          <div style={{ position: 'fixed', bottom: '80px', left: '1rem', right: '1rem', backgroundColor: '#fff', border: '1px solid #e5e7eb', borderRadius: '12px', boxShadow: '0 8px 32px rgba(0,0,0,0.15)', zIndex: 60, overflow: 'hidden' }}>
            <button onClick={handleGoogleCalendar} style={{ width: '100%', padding: '1rem', background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left', fontSize: '0.9rem', fontWeight: 500, color: '#111', display: 'flex', alignItems: 'center', gap: '0.75rem', borderBottom: '1px solid #f3f4f6' }}>
              🗓 Google Calendar
            </button>
            <button onClick={handleDownloadICS} style={{ width: '100%', padding: '1rem', background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left', fontSize: '0.9rem', fontWeight: 500, color: '#111', display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
              📥 Apple / Outlook (.ics)
            </button>
          </div>
        )}
        <a href={event.ticket_url || (isMobileDevice ? deepLink : 'https://sahayy.com/download')} target={event.ticket_url || isMobileDevice ? undefined : '_blank'} rel="noopener noreferrer" style={{ flex: 1, padding: '0.875rem', background: 'linear-gradient(135deg,#FF385C,#e11d48)', border: 'none', borderRadius: '10px', color: '#fff', fontWeight: 600, fontSize: '0.95rem', cursor: 'pointer', textDecoration: 'none', boxShadow: '0 4px 14px rgba(255,56,92,0.35)', textAlign: 'center' }}>
          {event.ticket_url ? 'Get Tickets' : 'Open in App'}
        </a>
      </div>

      <style>{`
        @media (min-width: 768px) { .mobile-bottom-bar { display: none !important; } .hero-carousel { display: none !important; } }
        @media (max-width: 767px) { .hero-grid { display: none !important; } .detail-columns { display: block !important; } .detail-sidebar { display: none !important; } }
      `}</style>
    </div>
  );
}
